import { useMemo } from 'react'

import { useForm, y } from '@circlefin/form'
import { Currency as CurrencyEnum } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { getCurrencyItems } from './CurrencyItems'

import type { FormDropdownProps } from '@circlefin/form/Form.Dropdown'

export const CurrencySchema = y.string().oneOf(Object.values(CurrencyEnum))

const schema = y.object({
  /**
   * Selected Currency.
   */
  currency: CurrencySchema,
  fromCurrency: CurrencySchema,
  toCurrency: CurrencySchema,
})

export type CurrencyFormValues = y.InferType<typeof schema>

export type CurrencyProps = Omit<
  FormDropdownProps<CurrencyFormValues>,
  'items' | 'variant'
> & {
  variant?: 'fiat' | 'crypto'
  itemsFilter?: CurrencyEnum[]
  name?: 'currency' | 'fromCurrency' | 'toCurrency'
}

// when items filter is not provided or is empty, use default filter
const DEFAULT_FILTER = [CurrencyEnum.USD, CurrencyEnum.EUR]

export const Currency: React.FC<CurrencyProps> = ({
  variant = 'crypto',
  label,
  itemsFilter = [],
  name = 'currency',
  ...props
}) => {
  const { t } = useTranslation('forms')
  const [Form] = useForm<CurrencyFormValues>({ schema })
  const items = useMemo(() => {
    const allItems = getCurrencyItems(variant)
    let effectiveItemsFilter

    if (variant === 'crypto') {
      const validItems = itemsFilter.filter((item) =>
        DEFAULT_FILTER.includes(item),
      )
      effectiveItemsFilter = validItems.length > 0 ? validItems : DEFAULT_FILTER // For crypto variant, use valid items or fallback to DEFAULT_FILTER
    } else {
      effectiveItemsFilter =
        itemsFilter.length > 0 ? itemsFilter : DEFAULT_FILTER // For fiat variant, use itemsFilter or DEFAULT_FILTER
    }

    return allItems.filter((currency) =>
      effectiveItemsFilter.includes(currency.value),
    )
  }, [variant, itemsFilter])

  return (
    <Form.Dropdown
      {...props}
      items={items}
      label={label ?? t`dropdown.common.currency.label`}
      name={name}
    />
  )
}
